import { QuestionType, Question } from '../types'
import { QuestionTypeConfig } from '../components/practice/renderers/types'

// Renderers
import { MultipleChoiceRenderer } from '../components/practice/renderers/MultipleChoiceRenderer'
import { ColumnArithmeticRenderer } from '../components/practice/renderers/ColumnArithmeticRenderer'
import { MoneyAssemblerRenderer } from '../components/practice/renderers/MoneyAssemblerRenderer'
import { ClockRenderer } from '../components/practice/renderers/ClockRenderer'
// New Renderers (will be implemented shortly)
import { FillInRenderer } from '../components/practice/renderers/FillInRenderer'
import { OrderingRenderer } from '../components/practice/renderers/OrderingRenderer'
import { MatchingRenderer } from '../components/practice/renderers/MatchingRenderer'
import { DragDropFillRenderer } from '../components/practice/renderers/DragDropFillRenderer'
import { TableReadingRenderer } from '../components/practice/renderers/TableReadingRenderer'
import { NumberSequenceRenderer } from '../components/practice/renderers/NumberSequenceRenderer'
import { RatioTableRenderer } from '../components/practice/renderers/RatioTableRenderer'
import { TextHighlightRenderer } from '../components/practice/renderers/TextHighlightRenderer'

// Helper for money validation
function validateMoneyAnswer(
  userAnswer: string,
  targetAmount: number,
): {
  isCorrect: boolean
  totalAmount: number
  distractorIndex: number
} {
  const selections = userAnswer
    .split(',')
    .map(Number)
    .filter((n) => !isNaN(n))
  const totalAmount = selections.reduce((sum, coin) => sum + coin, 0)
  const isCorrect = totalAmount === targetAmount
  let distractorIndex = 0
  if (!isCorrect) {
    const difference = Math.abs(totalAmount - targetAmount)
    if (totalAmount < targetAmount) {
      if (difference === 50) distractorIndex = 3
      else if (difference === 100) distractorIndex = 4
      else distractorIndex = 1
    } else {
      distractorIndex = 2
    }
  }
  return {
    isCorrect,
    totalAmount,
    distractorIndex,
  }
}

// Helper for money display
function formatMoneyDisplay(cents: number): string {
  const euros = Math.floor(cents / 100)
  const remainingCents = cents % 100
  if (remainingCents === 0) return `€${euros},-`
  return `€${euros},${remainingCents.toString().padStart(2, '0')}`
}

export const QUESTION_TYPE_REGISTRY: Record<QuestionType, QuestionTypeConfig> =
  {
    // STANDARD TYPES
    multiple_choice: {
      type: 'multiple_choice',
      label: 'Meerkeuze',
      isInteractive: false,
      shouldShuffleOptions: true,
      isCitoEligible: true,
      validateAnswer: (userAnswer, question) => ({
        isCorrect: Number(userAnswer) === question.correctAnswer,
        distractorIndex: Number(userAnswer),
      }),
      Renderer: MultipleChoiceRenderer,
    },

    // EXISTING INTERACTIVE TYPES
    cijferend_optellen: {
      type: 'cijferend_optellen',
      label: 'Cijferend Optellen',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const normalizedUserAnswer = String(userAnswer).replace(/\./g, '')
        const correctAnswer = question.options[question.correctAnswer].replace(
          /\./g,
          '',
        )
        const isCorrect = normalizedUserAnswer === correctAnswer

        let distractorIndex = question.options.findIndex(
          (opt) => opt.replace(/\./g, '') === normalizedUserAnswer,
        )
        if (distractorIndex === -1) distractorIndex = 0

        return { isCorrect, distractorIndex, displayAnswer: String(userAnswer) }
      },
      Renderer: ColumnArithmeticRenderer,
    },
    cijferend_aftrekken: {
      type: 'cijferend_aftrekken',
      label: 'Cijferend Aftrekken',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const normalizedUserAnswer = String(userAnswer).replace(/\./g, '')
        const correctAnswer = question.options[question.correctAnswer].replace(
          /\./g,
          '',
        )
        const isCorrect = normalizedUserAnswer === correctAnswer

        let distractorIndex = question.options.findIndex(
          (opt) => opt.replace(/\./g, '') === normalizedUserAnswer,
        )
        if (distractorIndex === -1) distractorIndex = 0

        return { isCorrect, distractorIndex, displayAnswer: String(userAnswer) }
      },
      Renderer: ColumnArithmeticRenderer,
    },
    geld_samenstellen: {
      type: 'geld_samenstellen',
      label: 'Geld Samenstellen',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const validation = validateMoneyAnswer(
          String(userAnswer),
          question.correctAnswer,
        )
        return {
          isCorrect: validation.isCorrect,
          distractorIndex: validation.distractorIndex,
          displayAnswer: formatMoneyDisplay(validation.totalAmount),
        }
      },
      Renderer: MoneyAssemblerRenderer,
    },
    klok_aflezen: {
      type: 'klok_aflezen',
      label: 'Klok Aflezen',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const correctAnswer = question.options[question.correctAnswer]
        const isCorrect = String(userAnswer) === correctAnswer
        let distractorIndex = question.options.findIndex(
          (opt) => opt === String(userAnswer),
        )
        if (!isCorrect && distractorIndex === -1) distractorIndex = 1

        return { isCorrect, distractorIndex, displayAnswer: String(userAnswer) }
      },
      Renderer: ClockRenderer,
    },

    // NEW INTERACTIVE TYPES
    fill_in: {
      type: 'fill_in',
      label: 'Invulvraag',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const config = question.interactiveConfig || {}
        const acceptedAnswers = config.acceptedAnswers || [
          String(question.correctAnswer),
        ]
        const caseSensitive = config.caseSensitive || false

        const userStr = String(userAnswer).trim()
        const isCorrect = acceptedAnswers.some((ans: string) =>
          caseSensitive
            ? ans === userStr
            : ans.toLowerCase() === userStr.toLowerCase(),
        )

        return {
          isCorrect,
          distractorIndex: isCorrect ? undefined : 0,
          displayAnswer: userStr,
        }
      },
      Renderer: FillInRenderer,
    },
    ordering: {
      type: 'ordering',
      label: 'Volgorde',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is comma-separated indices "0,1,2"
        const userIndices = String(userAnswer).split(',').map(Number)
        const config = question.interactiveConfig || {}
        const correctOrder = config.correctOrder || [] // e.g. [2, 0, 1]

        // Check if arrays match
        const isCorrect =
          userIndices.length === correctOrder.length &&
          userIndices.every((val, index) => val === correctOrder[index])

        return { isCorrect, distractorIndex: isCorrect ? undefined : 0 }
      },
      Renderer: OrderingRenderer,
    },
    matching: {
      type: 'matching',
      label: 'Koppelen',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is JSON string of pairs: "[[0,1],[1,0]]" (leftIndex, rightIndex)
        let userPairs: [number, number][] = []
        try {
          userPairs = JSON.parse(String(userAnswer))
        } catch (e) {
          return { isCorrect: false, distractorIndex: 0 }
        }

        // In matching, the index usually corresponds to the correct pair if set up simply
        // But we should check against interactiveConfig.pairs
        // For simplicity, we assume the pairs are defined in order in the config
        // and the user must match left[i] with right[i]

        // Check if all pairs are correct (left index == right index if sorted)
        // Or more robustly: check if each user pair exists in correct pairs

        // Simplified: Correct if every left item i is paired with right item i
        const isCorrect =
          userPairs.every(([left, right]) => left === right) &&
          userPairs.length === (question.interactiveConfig?.pairs?.length || 0)

        return { isCorrect, distractorIndex: isCorrect ? undefined : 0 }
      },
      Renderer: MatchingRenderer,
    },
    drag_drop_fill: {
      type: 'drag_drop_fill',
      label: 'Sleep en Plaats',
      isInteractive: true,
      shouldShuffleOptions: true, // Shuffle the drag options
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is JSON: { blankId: "value", ... }
        let userAnswers: Record<string, string> = {}
        try {
          userAnswers = JSON.parse(String(userAnswer))
        } catch (e) {
          return { isCorrect: false, distractorIndex: 0 }
        }

        const config = question.interactiveConfig || {}
        const blanks = config.blanks || []

        const isCorrect = blanks.every(
          (blank: any) => userAnswers[blank.id] === blank.correctAnswer,
        )

        return { isCorrect, distractorIndex: isCorrect ? undefined : 0 }
      },
      Renderer: DragDropFillRenderer,
    },
    table_reading: {
      type: 'table_reading',
      label: 'Tabel Aflezen',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        const config = question.interactiveConfig || {}
        const correctSelection = config.correctSelection

        const isCorrect = Number(userAnswer) === correctSelection

        return { isCorrect, distractorIndex: isCorrect ? undefined : 0 }
      },
      Renderer: TableReadingRenderer,
    },
    number_sequence: {
      type: 'number_sequence',
      label: 'Getallenreeks',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is comma-separated values for missing slots
        const userValues = String(userAnswer)
          .split(',')
          .map((s) => s.trim())
        const config = question.interactiveConfig || {}
        const correctValues = config.correctValues.map(String)

        const isCorrect =
          userValues.length === correctValues.length &&
          userValues.every((val, index) => val === correctValues[index])

        return {
          isCorrect,
          distractorIndex: isCorrect ? undefined : 0,
          displayAnswer: userValues.join(', '),
        }
      },
      Renderer: NumberSequenceRenderer,
    },
    ratio_table: {
      type: 'ratio_table',
      label: 'Verhoudingstabel',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is JSON string: { "row-col": "value" }
        let userInputs: Record<string, string> = {}
        try {
          userInputs = JSON.parse(String(userAnswer))
        } catch (e) {
          return { isCorrect: false, distractorIndex: 0 }
        }

        const config = question.interactiveConfig || {}
        const rows = config.rows || []
        const hiddenCells = config.hiddenCells || []

        // Normalize a value for numeric comparison
        // Handles: "1.200" (NL thousand sep), "1200", "1,5" (NL decimal), "1.5"
        const normalizeNumeric = (val: string): string => {
          let cleaned = val.trim().replace(/[€\s]/g, '')
          // If it contains both dots and commas, dots are thousand separators
          if (cleaned.includes('.') && cleaned.includes(',')) {
            cleaned = cleaned.replace(/\./g, '').replace(',', '.')
          } else if (cleaned.includes(',')) {
            // Comma is decimal separator (Dutch)
            cleaned = cleaned.replace(',', '.')
          } else if (cleaned.includes('.')) {
            // Could be thousand separator or decimal
            // If pattern is like "1.200" (digit.3digits), it's a thousand separator
            if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
              cleaned = cleaned.replace(/\./g, '')
            }
            // Otherwise keep as decimal
          }
          return cleaned
        }

        // Check each hidden cell
        const isCorrect = hiddenCells.every((cell: any) => {
          const key = `${cell.row}-${cell.col}`
          const userVal = userInputs[key] || ''
          const correctVal = String(rows[cell.row]?.[cell.col] || '')

          // Try exact match first
          if (userVal.trim() === correctVal.trim()) return true

          // Try numeric comparison
          const userNum = parseFloat(normalizeNumeric(userVal))
          const correctNum = parseFloat(normalizeNumeric(correctVal))
          if (!isNaN(userNum) && !isNaN(correctNum)) {
            return Math.abs(userNum - correctNum) < 0.001
          }

          return false
        })

        // Build display answer from user inputs
        const displayParts = hiddenCells.map((cell: any) => {
          const key = `${cell.row}-${cell.col}`
          return userInputs[key] || '?'
        })

        return {
          isCorrect,
          distractorIndex: isCorrect ? undefined : 0,
          displayAnswer: displayParts.join(', '),
        }
      },
      Renderer: RatioTableRenderer,
    },
    text_highlight: {
      type: 'text_highlight',
      label: 'Tekst Markeren',
      isInteractive: true,
      shouldShuffleOptions: false,
      isCitoEligible: false,
      validateAnswer: (userAnswer, question) => {
        // userAnswer is the selected segment ID (string)
        const config = question.interactiveConfig || {}
        const segments = config.segments || []

        // Find the selected segment
        const selectedSegmentIndex = segments.findIndex(
          (s: any) => s.id === userAnswer,
        )
        const selectedSegment = segments[selectedSegmentIndex]

        if (!selectedSegment) {
          return { isCorrect: false, distractorIndex: 0 }
        }

        const isCorrect = selectedSegment.isCorrect === true

        // Return the index of the segment as distractorIndex
        // This allows mapping to distractorExplanations if they are keyed by index
        return {
          isCorrect,
          distractorIndex: selectedSegmentIndex,
          displayAnswer: selectedSegment.text,
        }
      },
      Renderer: TextHighlightRenderer,
    },
  }

export function getQuestionTypeConfig(type: string): QuestionTypeConfig {
  return (
    QUESTION_TYPE_REGISTRY[type as QuestionType] ||
    QUESTION_TYPE_REGISTRY['multiple_choice']
  )
}
